/**
 * features/dashboard/AnnouncementsBanner.tsx
 * ------------------------------------------------------------------
 * Displays active announcements on the dashboard, filtered by the
 * user's role. Pinned first, urgent gets red accent. Dismissable.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
} from 'firebase/firestore';
import {
  Megaphone,
  Pin,
  X,
  AlertTriangle,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import type { Announcement, AnnouncementPriority, Role } from '@/types';

const PRIORITY_BORDER: Record<AnnouncementPriority, string> = {
  normal: 'border-l-blue-400',
  important: 'border-l-amber-400',
  urgent: 'border-l-red-500',
};

const PRIORITY_BG: Record<AnnouncementPriority, string> = {
  normal: '',
  important: 'bg-amber-50/50 dark:bg-amber-900/10',
  urgent: 'bg-red-50/50 dark:bg-red-900/10',
};

// Check against the user's FULL role set so targeted announcements reach
// users whose class-teacher / HOD / HOY role is an overlay on top of a
// 'subjectTeacher' primary role (otherwise classTeacher/hod targets miss them).
function canSeeTarget(roles: Role[], target: string): boolean {
  if (target === 'all') return true;
  if (!roles.length) return false;
  const has = (r: string) => roles.includes(r as Role);
  if (target === 'staff') return !has('parent');
  if (target === 'parents') return has('parent');
  if (target === 'admin') return has('admin') || has('headteacher');
  if (target === 'teachers') return ['admin', 'headteacher', 'editor', 'hoy', 'hod', 'classTeacher', 'subjectTeacher'].some(has);
  if (target === 'classTeachers') return has('classTeacher') || has('admin') || has('headteacher');
  if (target === 'hod') return has('hod') || has('admin') || has('headteacher');
  return true;
}

export default function AnnouncementsBanner() {
  const { userData, userRole } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Simple getDocs without orderBy to avoid needing a composite index
        const snap = await getDocs(collection(db, COLLECTIONS.announcements));
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Announcement);
        setAnnouncements(docs);
      } catch (e) {
        console.error('Failed to load announcements:', e);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const visible = useMemo(() => {
    const now = new Date();
    const uid = userData?.uid ?? '';
    return announcements
      .filter((a) => {
        if (a.expiresAt && new Date(a.expiresAt) < now) return false;
        if (a.dismissedBy?.includes(uid)) return false;
        if (dismissed.has(a.id)) return false;
        const effRoles = (userData?.roles?.length ? userData.roles : userRole ? [userRole] : []) as Role[];
        if (!canSeeTarget(effRoles, a.target)) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const po: Record<AnnouncementPriority, number> = { urgent: 0, important: 1, normal: 2 };
        return (po[a.priority] ?? 2) - (po[b.priority] ?? 2) || (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
      });
  }, [announcements, userData, userRole, dismissed]);

  async function dismiss(id: string) {
    setDismissed((prev) => new Set([...prev, id]));
    try {
      await updateDoc(doc(db, COLLECTIONS.announcements, id), {
        dismissedBy: arrayUnion(userData?.uid ?? ''),
      });
    } catch (e) {
      console.error('Failed to dismiss:', e);
    }
  }

  if (!loaded || visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-6">
      {visible.slice(0, 5).map((a) => (
        <div
          key={a.id}
          className={`rounded-none rounded-r-xl border border-gray-200 dark:border-gray-700 border-l-4 ${PRIORITY_BORDER[a.priority]} ${PRIORITY_BG[a.priority]} px-4 py-3`}
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">
              {a.priority === 'urgent' ? (
                <AlertTriangle size={16} className="text-red-500" />
              ) : (
                <Megaphone size={16} className="text-blue-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                {a.pinned && <Pin size={11} className="text-amber-500" />}
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">{a.title}</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{a.body}</p>
              <p className="text-xs text-gray-400 mt-1">
                {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}
                {a.createdByName && ` · ${a.createdByName}`}
              </p>
            </div>
            <button onClick={() => dismiss(a.id)} className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 shrink-0" title="Dismiss">
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
      {visible.length > 5 && (
        <p className="text-xs text-gray-400 text-center">+ {visible.length - 5} more</p>
      )}
    </div>
  );
}
